/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  formatNumber,
  formatPercent,
  formatCurrency,
  formatLargeNumber,
  formatLargeCurrency,
  formatPercentWithoutUnit,
} from "../../utils/format-number";
import { InstanceDataConfig, ValueFormatType } from "@/types/chart";
import { ChartOptions } from "chart.js";

const formatTooltip = ({
  context,
  formatters,
}: {
  context: any;
  formatters: ((value: string | number) => string)[];
}) => {
  const label = context.dataset.label;
  const value = context.parsed.y;
  const formatter = formatters[context.datasetIndex] || formatNumber;

  return `${label}: ${formatter(value)}`;
};

const formatterMap: Record<
  ValueFormatType,
  (value: string | number) => string
> = {
  none: (v: string | number) => v.toString(),
  number: formatNumber,
  percent: formatPercent,
  currency: formatCurrency,
  "large-number": formatLargeNumber,
  "large-currency": formatLargeCurrency,
};

const getDoubleAxisConfig = (position: "left" | "right") => {
  return {
    position,
    gridDisplay: position === "left" ? true : false,
    ticksPadding: position === "left" ? 12 : 0,
    titlePaddingBottom: position === "left" ? -5 : 0,
  };
};

/**
 * Converts the payload to options for the chart
 * @param title The title of the chart
 * @param valueFormatType The format type of the value
 * @returns The options for the chart
 */
export const convertInstanceConfigToOptions = ({
  instanceDataConfig,
}: {
  instanceDataConfig: InstanceDataConfig[];
}): {
  scales: ChartOptions<"bar" | "line">["scales"];
  plugins: ChartOptions<"bar" | "line">["plugins"];
} => {
  const scales = instanceDataConfig.reduce<
    ChartOptions<"bar" | "line">["scales"]
  >(
    (acc, { groupType, valueFormatType, yAxisID, unit, unitAxisType }) => {
      const formatter =
        valueFormatType === "percent"
          ? formatPercentWithoutUnit
          : formatterMap[valueFormatType];

      // The null check is to make TypesSript happy
      if (!acc) acc = {};

      const isLeftAxis = yAxisID === "y";
      const doubleAxisConfig = getDoubleAxisConfig(
        isLeftAxis ? "left" : "right"
      );

      acc[yAxisID] = {
        stacked: groupType === "stack",
        position: doubleAxisConfig.position,
        beginAtZero: true,
        grid: {
          display: doubleAxisConfig.gridDisplay,
        },
        title: {
          display: !!unit && unitAxisType === "side",
          text: `(${unit})`,
          padding: { bottom: doubleAxisConfig.titlePaddingBottom },
        },
        border: {
          display: false,
        },
        ticks: {
          callback: (value: string | number) => formatter(value),
          padding: doubleAxisConfig.ticksPadding,
        },
      };
      return acc;
    },
    {
      x: {
        stacked: instanceDataConfig.some(
          ({ groupType }) => groupType === "stack"
        ),
        grid: {
          display: false,
        },
      },
    }
  );

  const plugins: ChartOptions<"bar" | "line">["plugins"] = {
    tooltip: {
      caretSize: instanceDataConfig.length > 1 ? 0 : undefined,
      caretPadding: instanceDataConfig.length > 1 ? 15 : 0,
      callbacks: {
        label: (context) =>
          formatTooltip({
            context,
            formatters: instanceDataConfig.map(
              ({ valueFormatType }) => formatterMap[valueFormatType]
            ),
          }),
      },
    },
  };

  return {
    scales,
    plugins,
  };
};
